import { buildCookieHeader, readAllowedCookies } from './cookies';
import type { ChatResponse, SessionResponse } from './types';

const FORWARDED_COOKIE_HEADER = 'X-Orca-Cookie';

export interface ChatRequestBody {
  message: string;
  conversationId?: string;
  inputs?: Record<string, string>;
  confirmed?: boolean;
  pageUrl?: string;
}

export interface ConfirmRequestBody {
  confirmationId: string;
  inputs?: Record<string, string>;
  pageUrl?: string;
}

export interface HistoryMessage {
  role: 'user' | 'assistant' | 'system' | 'error';
  text: string;
}

export interface HistoryResponse {
  conversationId?: string;
  messages?: HistoryMessage[];
}

export class OrchestratorClient {
  constructor(private readonly baseUrl: string) {}

  private headers(): Record<string, string> {
    const cookieHeader = buildCookieHeader(readAllowedCookies());
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (cookieHeader) {
      headers[FORWARDED_COOKIE_HEADER] = cookieHeader;
    }
    return headers;
  }

  async chat(body: ChatRequestBody): Promise<ChatResponse> {
    return this.post('/orchestrator/chat', body);
  }

  async confirm(body: ConfirmRequestBody): Promise<ChatResponse> {
    return this.post('/orchestrator/confirm', body);
  }

  /**
   * Best-effort fetch of prior transcript for a conversation, used to rehydrate the panel when a
   * visitor moves between apps/subdomains and only a shared conversation id cookie survived (no
   * local transcript cache). Callers should tolerate this throwing if the orchestrator hasn't
   * implemented the endpoint yet.
   */
  async getHistory(conversationId: string): Promise<HistoryResponse> {
    const query = `?conversationId=${encodeURIComponent(conversationId)}`;
    const response = await fetch(`${this.baseUrl.replace(/\/$/, '')}/orchestrator/history${query}`, {
      method: 'GET',
      headers: this.headers(),
      credentials: 'include',
    });

    const payload = (await response.json().catch(() => ({}))) as HistoryResponse & {
      message?: string;
    };

    if (!response.ok) {
      throw new Error(payload.message ?? `Request failed (${response.status})`);
    }

    return payload;
  }

  async getSession(conversationId?: string): Promise<SessionResponse> {
    const query = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
    const response = await fetch(`${this.baseUrl.replace(/\/$/, '')}/orchestrator/session${query}`, {
      method: 'GET',
      headers: this.headers(),
      credentials: 'include',
    });

    const payload = (await response.json().catch(() => ({}))) as SessionResponse & {
      message?: string;
    };

    if (!response.ok) {
      throw new Error(payload.message ?? `Request failed (${response.status})`);
    }

    return payload;
  }

  private async post(path: string, body: unknown): Promise<ChatResponse> {
    const response = await fetch(`${this.baseUrl.replace(/\/$/, '')}${path}`, {
      method: 'POST',
      headers: this.headers(),
      credentials: 'include',
      body: JSON.stringify(body),
    });

    const payload = (await response.json().catch(() => ({}))) as ChatResponse & {
      error?: { message?: string };
      message?: string;
    };

    if (!response.ok) {
      throw new Error(payload.message ?? payload.error?.message ?? `Request failed (${response.status})`);
    }

    return payload;
  }
}
